import type { RoleName } from "@/lib/permissions";
import type * as T from "../types";
import type { components } from "./schema";

type Schemas = components["schemas"];
export type SessionWire = Schemas["SessionResponse"];
export type OrganisationWire = Schemas["BffOrganisation"];

const YEAR_STATUS: Record<Schemas["PeriodStatusCode"], T.FiscalYear["status"]> = {
  open: "open",
  softClosed: "locked",
  hardClosed: "closed",
};

/** "Priya Sharma" → "PS"; a one-word name or an address gives its first two letters. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0];
  if (first === undefined) return "?";
  const last = words[1] === undefined ? undefined : words[words.length - 1];
  return (last === undefined ? first.slice(0, 2) : first.charAt(0) + last.charAt(0)).toUpperCase();
}

/**
 * The API's session and the person's organisations, as the shell reads them.
 *
 * An organisation with no company profile yet still has to render a shell, so it falls back to the
 * name the identity service holds. The organisation list comes from a call that may have failed,
 * in which case the picker offers only where the person already is.
 */
export function toSession(
  wire: SessionWire,
  organisations: readonly OrganisationWire[],
): T.Session {
  const profile = wire.organisation;
  const membership = organisations.find((o) => o.id === wire.tenantId);
  const name =
    profile?.tradingName ?? profile?.legalName ?? membership?.name ?? "Your organisation";
  const fullName = wire.user.name ?? wire.user.email ?? "";
  const year = wire.fiscalYear;

  return {
    user: {
      id: wire.user.id ?? "",
      fullName,
      email: wire.user.email ?? "",
      initials: initialsOf(fullName),
      role: (wire.user.role ?? "View-only") as RoleName,
    },
    organization: {
      id: wire.tenantId,
      name,
      legalName: profile?.legalName ?? name,
      vatRegistered: profile?.taxRegistered ?? false,
      country: profile?.countryCode ?? "",
      currency: profile?.homeCurrency ?? "",
      sampleData: profile?.sampleData ?? false,
    },
    organizations:
      organisations.length > 0
        ? organisations.map((o) => ({ id: o.id, name: o.name, demo: o.demo ?? false }))
        : [{ id: wire.tenantId, name }],
    permissions: wire.permissions as T.Permission[],
    fiscalYear:
      year === null
        ? null
        : {
            id: year.id,
            label: `FY ${year.code}`,
            start: year.startsOn,
            end: year.endsOn,
            status: YEAR_STATUS[year.status],
            isCurrent: true,
          },
    calendar: wire.calendar === "bikram-sambat" ? "bikram-sambat" : "gregorian",
    documentPdf: wire.documentPdf ?? false,
    documentMail: wire.documentMail ?? false,
    invitations: wire.invitations ?? false,
    taxNames: {
      registration: wire.taxNames.registration,
      valueAdded: wire.taxNames.valueAdded,
      withholding: wire.taxNames.withholding,
      packName: wire.taxNames.packName ?? null,
    },
  };
}
